import React from 'react';
import { 
  FaChartLine, FaArrowUp, FaArrowDown, FaCalendarAlt, 
  FaCoins, FaCheckCircle, FaEye, FaChevronRight 
} from 'react-icons/fa';
import { 
  getLast3MonthsIncomeSummary, 
  getYearMonth, 
  getTodayDateStr 
} from './billTrackerData';

const Last3MonthsEarningsCard = ({
  incomeStreams,
  referenceDate,
  onSelectMonth
}) => {
  const activeYearMonth = getYearMonth(referenceDate || getTodayDateStr());
  const summary = getLast3MonthsIncomeSummary(incomeStreams, activeYearMonth);
  const { months, total3Months, avgCompleted, priorMoM } = summary;

  if (!months || months.length === 0) return null;

  // Maximum actual amount across the 3 months to scale visual bar heights
  const maxActual = Math.max(...months.map(m => m.totalActual), 1);

  return (
    <div className="bt-card bt-earnings-comparison-card" style={{ marginBottom: '24px' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: '12px',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '42px',
            height: '42px',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            fontSize: '18px',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)'
          }}>
            <FaCoins />
          </div>
          <div>
            <h3 style={{
              margin: 0,
              fontSize: '1.05rem',
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: '0.02em',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              3-Month Earnings Activity & Actual Received Comparison
            </h3>
            <p className="bt-subtext" style={{ margin: '3px 0 0 0' }}>
              Historical cashflow realization across the last 3 consecutive months
            </p>
          </div>
        </div>

        {/* Header Badges */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <div className="bt-pill-badge" style={{ backgroundColor: '#ecfdf5', borderColor: '#a7f3d0', color: '#065f46', fontSize: '0.8rem', padding: '4px 10px' }}>
            <FaChartLine />
            <span>3-Mo Total: <strong>${total3Months.toLocaleString()}</strong></span>
          </div>

          <div className="bt-pill-badge" style={{ backgroundColor: '#f0fdf4', borderColor: '#bbf7d0', color: '#15803d', fontSize: '0.8rem', padding: '4px 10px' }}>
            <span>Prior 2-Mo Avg: <strong>${avgCompleted.toLocaleString()}/mo</strong></span>
          </div>
        </div>
      </div>

      {/* 3-Month Visual Comparison Columns */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: '16px',
        marginBottom: '20px'
      }}>
        {months.map((m, index) => {
          const isSelected = m.key === activeYearMonth;
          const barHeightPercent = maxActual > 0 ? Math.max(12, Math.round((m.totalActual / maxActual) * 100)) : 10;
          const isCurrentRealMonth = m.isCurrent;

          return (
            <div
              key={m.key}
              style={{
                borderRadius: '14px',
                border: isSelected ? '2px solid #10b981' : '1px solid #e2e8f0',
                backgroundColor: isSelected ? '#f0fdf4' : '#ffffff',
                padding: '16px 18px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                position: 'relative',
                boxShadow: isSelected ? '0 4px 14px rgba(16, 185, 129, 0.12)' : '0 1px 3px rgba(0,0,0,0.02)',
                transition: 'all 0.2s ease'
              }}
            >
              {/* Top tag / pill */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <FaCalendarAlt style={{ color: isSelected ? '#059669' : '#64748b', fontSize: '13px' }} />
                  <strong style={{ fontSize: '0.95rem', color: '#0f172a' }}>{m.label}</strong>
                </div>

                {isCurrentRealMonth ? (
                  <span style={{
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: '12px',
                    backgroundColor: '#dbeafe',
                    color: '#1d4ed8',
                    textTransform: 'uppercase'
                  }}>
                    {m.totalActual === 0 ? 'Fresh / New Month' : 'In Progress'}
                  </span>
                ) : (
                  <span style={{
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: '12px',
                    backgroundColor: '#f1f5f9',
                    color: '#475569',
                    textTransform: 'uppercase'
                  }}>
                    Completed
                  </span>
                )}
              </div>

              {/* Big Actual Received Metric */}
              <div style={{ marginBottom: '14px' }}>
                <span style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>
                  Actual Received ($)
                </span>
                <div style={{
                  fontSize: '1.65rem',
                  fontWeight: 800,
                  color: m.totalActual > 0 ? '#059669' : '#64748b',
                  letterSpacing: '-0.02em',
                  marginTop: '2px'
                }}>
                  ${m.totalActual.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                  <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    Expected: <strong>${m.totalExpected.toLocaleString()}</strong>
                  </span>
                  <span style={{
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: m.realizationRate >= 100 ? '#059669' : (m.realizationRate > 0 ? '#d97706' : '#94a3b8')
                  }}>
                    {m.realizationRate}% realized
                  </span>
                </div>
              </div>

              {/* Progress Visual Bar */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{
                  height: '10px',
                  backgroundColor: '#e2e8f0',
                  borderRadius: '5px',
                  overflow: 'hidden'
                }}>
                  <div
                    style={{
                      height: '100%',
                      width: `${barHeightPercent}%`,
                      backgroundColor: m.totalActual > 0 ? (isSelected ? '#10b981' : '#34d399') : '#cbd5e1',
                      borderRadius: '5px',
                      transition: 'width 0.4s ease'
                    }}
                  />
                </div>
              </div>

              {/* Stream Breakdown Sub-list */}
              <div style={{
                backgroundColor: isSelected ? '#ffffff' : '#f8fafc',
                borderRadius: '8px',
                padding: '8px 12px',
                border: '1px solid',
                borderColor: isSelected ? '#bbf7d0' : '#f1f5f9',
                marginBottom: '14px',
                fontSize: '0.78rem'
              }}>
                <div style={{ fontWeight: 700, color: '#475569', marginBottom: '6px', fontSize: '0.72rem', textTransform: 'uppercase' }}>
                  Inflow By Stream
                </div>
                {m.streams.map(st => (
                  <div key={st.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', color: '#334155' }}>
                    <span style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '140px' }} title={st.name}>
                      {st.name}
                    </span>
                    <strong style={{ color: st.actual > 0 ? '#059669' : '#64748b' }}>
                      ${st.actual.toLocaleString(undefined, { minimumFractionDigits: 0 })}
                    </strong>
                  </div>
                ))}
              </div>

              {/* Bottom Action Button: Switch to this month */}
              <div>
                {isSelected ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '6px 12px',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    color: '#059669',
                    backgroundColor: '#dcfce7',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}>
                    <FaCheckCircle size={12} /> Currently Viewing
                  </div>
                ) : (
                  <button
                    className="bt-btn-secondary"
                    style={{
                      width: '100%',
                      padding: '6px 12px',
                      fontSize: '0.8rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      cursor: 'pointer'
                    }}
                    onClick={() => onSelectMonth && onSelectMonth(m.key)}
                    title={`View ledger & income for ${m.label}`}
                  >
                    <FaEye size={12} /> View {m.shortLabel} Ledger <FaChevronRight size={10} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Strategic Trend Footer Note */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '12px',
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        padding: '10px 16px',
        fontSize: '0.84rem',
        color: '#475569'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FaCheckCircle style={{ color: '#10b981' }} />
          <span>
            {months[1]?.label}: <strong>${months[1]?.totalActual.toLocaleString()}</strong> realized ({months[1]?.realizationRate}% of target). 
            {' '}{months[2]?.label} has refreshed for the new month with <strong>${months[2]?.totalExpected.toLocaleString()}</strong> in scheduled receivables.
          </span>
        </div>

        {priorMoM !== 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 700, color: priorMoM >= 0 ? '#059669' : '#d97706' }}>
            {priorMoM >= 0 ? <FaArrowUp size={11} /> : <FaArrowDown size={11} />}
            <span>Prior MoM Change: {priorMoM > 0 ? `+${priorMoM}%` : `${priorMoM}%`}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default Last3MonthsEarningsCard;
